import { useEffect, useState, memo, useCallback } from "react";
import "./DesktopSubjectItem.css";
import { IOfferedSubjectData, IOfferedSubjectSection, IQuota, ISelectedSection } from "../../types";
import { useSelector } from "react-redux";
import { selectStudent } from "../../reduxSlices/studentSlice";
import {
  selectSubcriptionProcessSectionIds,
  setDetailSubjectOffered,
  selectFailedSubcriptionIds,
} from "../../reduxSlices/subjectsSlice";
import { useAppDispatch } from "../../app/store";
import {
  selectFlags,
  selectInTransitInRquest,
  selectMobileFeatures,
  setPanelFilters,
} from "../../reduxSlices/globalFlagsSlice";
import ProfessorAndSubjectHours from "../ProfessorAndSubjectHours/ProfessorAndSubjectHours";
import { useNavigate } from "react-router-dom";
import QuotaCountIcon from "../QuotaCountIcon/QuotaCountIcon";
import ButtonInscription from "../ButtonInscription/ButtonInscription";
import { extraerNombreMateria, extraerSeccion, findProfeTitular } from "../../utils";
import ButtonPin from "../ButtonPin/ButtonPin";
import ButtonCalendarize from "../ButtonCalendarize/ButtonCalendarize";
import AnimationWrapper from "./AnimationWrapper";
import { useTranslatedText } from "../../contexts/TranslationContext";
import Translatable from "../Translatable/Translatable";

interface IPropsSubjectItem {
  id: number;
  subjectId: number;
  section: string;
  subjectData: IOfferedSubjectData;
  quota: IQuota;
  onChange?: (data: ISelectedSection) => Promise<any>;
  pinned: boolean;
  creditos: number;
  sectionType: string;
  idMateria: number;
  turno: string;
  subject: IOfferedSubjectSection;
  disableAnimations?: boolean;
  courseInProcess?: boolean;
}

const DesktopSubjectItem = ({
  section,
  id,
  subjectData,
  quota,
  onChange,
  subjectId,
  pinned,
  creditos,
  sectionType,
  idMateria,
  turno,
  subject,
  disableAnimations = false,
  courseInProcess = false,
}: IPropsSubjectItem) => {
  const student = useSelector(selectStudent);
  const inTransitInscriptionRequet = useSelector(selectInTransitInRquest);
  const mobileFeatures = useSelector(selectMobileFeatures);
  const { collapseCards, collapseCardsSinCupo } = useSelector(selectFlags);
  const dispatch = useAppDispatch();
  const [hasClick, setHasClick] = useState(false);
  const [inscriptionConfirmed, setInscriptionConfirmed] = useState(false);
  const subcriptionProcessSectionIds = useSelector(selectSubcriptionProcessSectionIds);
  const failedSubcriptionIds = useSelector(selectFailedSubcriptionIds);
  const [failedInscription, setFailedInscription] = useState(false);
  const [isSubInProcess, setIsSubInProcess] = useState(false);

  const subType = sectionType === "OPTATIVO" ? "Optativas" : "Obligatorias";
  const subjectName = useTranslatedText(extraerNombreMateria(section), { titleCase: true });
  const subTypeLabel = useTranslatedText(subType, { capitalize: true });
  const loadingQuotasLabel = useTranslatedText("Cargando cupos");
  const creditsLabel = useTranslatedText("Créditos", { capitalize: true });
  const shiftLabel = useTranslatedText("Turno", { capitalize: true });

  const idCurso = subjectData.reserveData.idS;
  const anioLectivo = subjectData.reserveData.aL;
  /* -------------------------------------------------------------------------- */
  /*                                   EFFECTS                                  */
  /* -------------------------------------------------------------------------- */

  useEffect(() => {
    if (inTransitInscriptionRequet && hasClick) {
      setTimeout(() => {
        setHasClick(false);
      }, 10000);
    }
  }, [inTransitInscriptionRequet]);

  /* ------------------- Failed in validation response CLOUD ------------------ */
  useEffect(() => {
    if (failedInscription) {
      setTimeout(() => {
        setFailedInscription(false);
      }, 2000); // Aumentado de 500ms a 600ms para evitar parpadeo
    }
  }, [failedInscription]);
  /* ---------------------- Failed in response Web Socket --------------------- */
  useEffect(() => {
    if (failedSubcriptionIds.some((item) => item.idCurso === idCurso && item.anioLectivo === anioLectivo)) {
      setFailedInscription(true);
    }
  }, [failedSubcriptionIds]);

  useEffect(() => {
    const isProcessing = Array.isArray(subcriptionProcessSectionIds)
      ? subcriptionProcessSectionIds.some((item) => item.idCurso === idCurso && item.anioLectivo === anioLectivo)
      : false;
    if (isProcessing) {
      setIsSubInProcess(true);
    } else {
      setIsSubInProcess(false);
    }
  }, [subcriptionProcessSectionIds]);

  /* -------------------------------------------------------------------------- */
  /*                                  HANDLERS                                  */
  /* -------------------------------------------------------------------------- */

  const handleModalPanelInfo = useCallback(
    (subjectId: number, cursoId: number) => {
      dispatch(setDetailSubjectOffered({ subjectId, cursoId }));
      dispatch(setPanelFilters(false));
    },
    [dispatch]
  );

  const idCupoValid = subjectData?.reserveData?.idC !== -1;

  return (
    <div className={`desktop-subject-item-container ${courseInProcess ? "offer-on-process-subject-item" : ""}`}>
      <div
        className={`desktop-subject-item overflow-hidden ${pinned ? "desktop-pinned-subject" : ""} ${
          quota && quota.availability === 0 && student.prioridad && !isSubInProcess ? "bg-gray-noquota" : ""
        } ${inscriptionConfirmed ? "hidden-card" : ""}`}
      >
        <AnimationWrapper
          isSubInProcess={disableAnimations ? false : isSubInProcess}
          failedInscription={disableAnimations ? false : failedInscription}
        >
          {mobileFeatures && idCupoValid && !quota.readyToUse && student.prioridad !== false && (
            <div className="flex items-center gap-1 mb-1" style={{ zIndex: 1 }}>
              <p className="text-blue-500 whitespace-nowrap text-[12px]">{loadingQuotasLabel}</p>
              <div className="w-3 h-3 min-w-[12px] border-2 border-blue-200 border-t-blue-500 rounded-full animate-spin"></div>
            </div>
          )}
          <div style={{ zIndex: 1 }} className="desktop-item-first-row">
            {quota && quota?.availability >= 0 && student.prioridad && (quota.readyToUse || !idCupoValid) && (
              <>
                <QuotaCountIcon
                  qty={quota.qty}
                  subjectData={subjectData}
                  availability={quota.availability}
                  idMateria={idMateria}
                  quota={quota}
                  anioLectivo={subject.anioLectivo}
                />
              </>
            )}
            {!mobileFeatures && idCupoValid && !quota.readyToUse && student.prioridad !== false && (
              <div className="flex items-center justify-center gap-1">
                <p className="text-blue-500 whitespace-nowrap text-sm">{loadingQuotasLabel}</p>
                <div className="w-4 h-4 border-[3px] border-blue-200 border-t-blue-500 rounded-full animate-spin"></div>
              </div>
            )}
            <div className="title-column-card w-3/6">
              {mobileFeatures ? (
                <div
                  className={`flex flex-row hover:underline hover:cursor-pointer ${
                    quota && quota.availability === 0 && student.prioridad && !isSubInProcess ? "text-gray" : ""
                  }`}
                  onClick={() => handleModalPanelInfo(Number(subjectId), Number(id))}
                >
                  <p
                    title={`${subjectName}`}
                    className={`desktop-item-title desktop-item-section max-w-fit`}
                  >
                    {`${subjectName} `}
                  </p>
                  <p className={`desktop-item-title max-w-fit`}>&nbsp;{`- ${extraerSeccion(section)}`}</p>
                </div>
              ) : (
                <div
                  className={`hover:underline hover:cursor-pointer ${
                    quota && quota.availability === 0 && student.prioridad && !isSubInProcess ? "text-gray" : ""
                  }`}
                  onClick={() => handleModalPanelInfo(Number(subjectId), Number(id))}
                >
                  <p title={`${subjectName} - ${extraerSeccion(section)}`} className="flex gap-[2px] font-bold text-[18px]">
                    <span className="truncate overflow-ellipsis">{subjectName}</span>
                    <span className="w-min whitespace-nowrap pl-1">- {extraerSeccion(section)}</span>
                  </p>
                </div>
              )}
              {!courseInProcess && (
                <p className="mobile-profesor-titular">
                  {findProfeTitular(subjectData) ?? <Translatable as="span" capitalize>A designar</Translatable>}
                </p>
              )}
            </div>
            <div className="desktop-items-actions">
              {!courseInProcess && (
                <>
                  <div className="item-actions-circles">
                    {pinned && <p className="subject-item-type">{subTypeLabel}</p>}
                    <ButtonPin subject={subject} />
                    <ButtonCalendarize subject={subject} />
                  </div>
                  <ButtonInscription id={id} idMateria={idMateria} subjectData={subjectData} quota={quota} />
                </>
              )}
            </div>
          </div>
          <div
            className={`${
              collapseCards || courseInProcess
                ? "hide-collapse-info"
                : collapseCardsSinCupo && quota && quota.availability === 0
                ? "hide-collapse-info"
                : ""
            } bottom-car-section flex-grow`}
          >
            <div className="divider-card"></div>
            {/* ------------------------ horarios desktop ----------------------- */}
            {!mobileFeatures && (
              <ProfessorAndSubjectHours
                infoFechas={subject.infoFechas}
                modalidad={subject.modalidad}
                turno={turno}
                creditos={creditos}
                subjectData={subjectData}
                isDrawerContent={false}
              />
            )}
            {/* ----------------------------- horarios mobile ---------------------------- */}
            {mobileFeatures && (
              <div className={`flex-grow max-h-24 overflow-y-auto flex items-center gap-2 relative`}>
                {/* <div className={`sticky border-2 w-full h-4 z-10 ${gradientClass()}} bottom-0`}></div> */}
                <ProfessorAndSubjectHours
                  infoFechas={subject.infoFechas}
                  subjectData={subjectData}
                  isDrawerContent={false}
                />
                <div className="w-[25%] space-y-2 py-3 h-full">
                  {creditos > 0 && (
                    <div className="" style={{ zIndex: "1" }}>
                      <h4 className="text-[#B7B7B7] text-[14px]">{creditsLabel}</h4>
                      <p className="text-[14px]">{creditos}</p>
                    </div>
                  )}
                  {!creditos && turno && (
                    <div className="" style={{ zIndex: "1" }}>
                      <h4 className="text-[#B7B7B7] text-[14px]">{shiftLabel}</h4>
                      <p className="text-[14px]">{turno}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ------------------------------------ . ----------------------------------- */}
          </div>

          {mobileFeatures && !courseInProcess && (
            <div className="z-[1] flex items-center justify-end gap-[8px]">
              {pinned && <p className="subject-item-type">{subTypeLabel}</p>}
              <ButtonPin subject={subject} />
              <ButtonCalendarize subject={subject} />
              <ButtonInscription id={id} idMateria={idMateria} subjectData={subjectData} quota={quota} />
            </div>
          )}
        </AnimationWrapper>
      </div>
    </div>
  );
};

export default memo(DesktopSubjectItem);
